import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import BaseDirectory from "files/types/enums/baseDirectory";
import {
  Files,
  LayersPlus,
  LayoutDashboard,
  MessageSquare,
  Plus,
  Settings,
  SquareTerminal,
  Workflow,
} from "lucide-react";
import {
  useWorkflowsAccessFlag,
  useWorksheetsFlag,
} from "rwb/pages/project/hooks/usePipelinesFlag";
import { WorkflowSubmissionStrings } from "rwb/pages/project/types/classes/WorkflowSubmissionStrings";
import RoutePath from "rwb/pages/project/types/enums/routePath";
import { Skeleton } from "~/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import { SectionNavGroupLabel } from "~/pages/manifold-nextgen/components/SectionNavGroupLabel";
import {
  SectionNavLink,
  sectionNavLinkClassName,
} from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionNavPlaceholder } from "~/pages/manifold-nextgen/components/SectionNavPlaceholder";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { AgentAvatarStack } from "~/pages/manifold-nextgen/components/agents/AgentAvatarStack";
import {
  nextgenConversationPath,
  nextgenProjectChatsPath,
  nextgenProjectFeaturedSessionPath,
  nextgenProjectPath,
  nextgenProjectSessionsPath,
  nextgenProjectSettingsPath,
  nextgenProjectWorkbenchPath,
} from "~/pages/manifold-nextgen/paths";
import { Agent } from "~/services/agents";
import { FeaturedSession, ProjectConversation } from "~/services/projects";
import { ConversationActionsMenu } from "./ConversationActionsMenu";
import { DeleteConversationDialog } from "./DeleteConversationDialog";
import { ShareConversationDialog } from "./ShareConversationDialog";
import { ComputeEnvsNavLabel } from "./compute/ComputeEnvsNavLabel";
import {
  useFeaturedSessionAgents,
  useFeaturedSessionServedAgents,
} from "./useFeaturedSessionAgents";
import { useFeaturedSessionsMutations } from "./useFeaturedSessionsMutations";
import { useSessionActions } from "./useSessionActions";

export interface ProjectSidebarProps {
  projectId: string;
  // The project's Research Workbench, or null for a project created before
  // provisioning existed — its workbench-backed views stay inert placeholders.
  workbenchId: number | null;
  sessions: ProjectConversation[] | undefined;
  featuredSessions: FeaturedSession[] | undefined;
  attachedAgentsWithDetails: Agent[] | undefined;
  isOwner: boolean;
}

// How many sessions the sidebar shows before collapsing behind "View all".
const SESSIONS_SIDEBAR_LIMIT = 5;

interface SessionRowProps {
  projectId: string;
  session: ProjectConversation;
  // Resolved from `session.agent_ids` against the project's agent roster.
  // Rendered as a stack only when there's more than one; otherwise the
  // single-agent path below reads `session.agent` as before.
  agents: Agent[];
  isRenaming: boolean;
  onStartRename: () => void;
  onCommitRename: (next: string) => void;
  onEndRename: () => void;
  onAutoRename: () => void;
  isAutoRenaming: boolean;
  onShare: () => void;
  onDelete: () => void;
  // Whether the current viewer authored this session — non-owners get no
  // rename/share/delete actions, since it isn't theirs to manage.
  isOwnSession: boolean;
}

const SessionRow = ({
  projectId,
  session,
  agents,
  isRenaming,
  onStartRename,
  onCommitRename,
  onEndRename,
  onAutoRename,
  isAutoRenaming,
  onShare,
  onDelete,
  isOwnSession,
}: SessionRowProps) => {
  const [draft, setDraft] = useState(session.display_name);
  const inputRef = useRef<HTMLInputElement>(null);

  const [flashing, setFlashing] = useState(false);
  const prevNameRef = useRef(session.display_name);

  useEffect(() => {
    if (prevNameRef.current === session.display_name) {
      return;
    }
    prevNameRef.current = session.display_name;
    setFlashing(true);
    const id = window.setTimeout(() => setFlashing(false), 900);
    return () => window.clearTimeout(id);
  }, [session.display_name]);

  useEffect(() => {
    if (isRenaming) {
      setDraft(session.display_name);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [isRenaming, session.display_name]);

  const commit = () => {
    onCommitRename(draft);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onEndRename();
    }
  };

  const location = useLocation();
  const targetPath = nextgenConversationPath(projectId, session.id);
  const isActive = location.pathname === targetPath;

  return (
    <li className="group relative cursor-pointer">
      <NavLink
        to={targetPath}
        className={cn(
          "flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors",
          isActive
            ? "bg-black/[0.06] text-foreground dark:bg-white/[0.08]"
            : "text-muted-foreground hover:bg-black/[0.06] hover:text-foreground dark:hover:bg-white/[0.08]"
        )}
        onClick={(e) => {
          if (isRenaming) {
            e.preventDefault();
          }
        }}
      >
        {agents.length > 1 ? (
          <AgentAvatarStack agents={agents} size={18} />
        ) : session.agent ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0">
                <AgentAvatar agent={session.agent} size={18} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{session.agent.display_name}</TooltipContent>
          </Tooltip>
        ) : (
          <div
            className="flex size-[18px] shrink-0 items-center justify-center text-current"
            aria-hidden="true"
          >
            <MessageSquare className="size-3" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            <input
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={commit}
              onClick={(e) => e.preventDefault()}
              aria-label="Rename session"
              className="w-full rounded border border-border bg-background px-1 py-0 text-xs font-medium text-foreground outline-none focus:border-primary"
            />
          ) : (
            <span
              className={cn(
                "block truncate pr-4 text-sm font-medium transition-colors duration-700",
                flashing && "text-primary"
              )}
            >
              {session.display_name}
            </span>
          )}
        </div>
      </NavLink>

      {!isRenaming && isOwnSession && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <ConversationActionsMenu
            onShare={onShare}
            onRename={onStartRename}
            onAutoRename={onAutoRename}
            isAutoRenaming={isAutoRenaming}
            onDelete={onDelete}
          />
        </div>
      )}
    </li>
  );
};

const RecentSessionsList = ({
  projectId,
  sessions,
  isOwner,
  currentUserEmail,
  featuredSessions,
  attachedAgentsWithDetails,
}: {
  projectId: string;
  sessions: ProjectConversation[];
  isOwner: boolean;
  currentUserEmail: string | undefined;
  featuredSessions: FeaturedSession[] | undefined;
  attachedAgentsWithDetails: Agent[] | undefined;
}) => {
  const agentsById = new Map((attachedAgentsWithDetails ?? []).map((a) => [a.id, a]));
  const {
    renamingId,
    startRename,
    commitRename,
    endRename,
    autoRenamingId,
    autoRename,
    actionTarget,
    setActionTarget,
    confirmDelete,
  } = useSessionActions(projectId);

  return (
    <>
      <ul className="flex flex-col gap-0.5" role="list">
        {sessions.map((session) => (
          <SessionRow
            key={session.id}
            projectId={projectId}
            session={session}
            agents={(session.agent_ids ?? [])
              .map((id) => agentsById.get(id))
              .filter((a): a is Agent => a != null)}
            isRenaming={renamingId === session.id}
            onStartRename={() => startRename(session.id)}
            onCommitRename={(next) => commitRename(session, next)}
            onEndRename={endRename}
            onAutoRename={() => void autoRename(session)}
            isAutoRenaming={autoRenamingId === session.id}
            onShare={() => setActionTarget({ session, action: "share" })}
            onDelete={() => setActionTarget({ session, action: "delete" })}
            isOwnSession={session.user_id === currentUserEmail}
          />
        ))}
      </ul>

      <ShareConversationDialog
        open={actionTarget?.action === "share"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationId={actionTarget?.session.id ?? ""}
        projectId={projectId}
        isOwner={isOwner}
        displayName={actionTarget?.session.display_name ?? ""}
        agentId={actionTarget?.session.agent_id}
        featuredSessions={featuredSessions}
      />

      <DeleteConversationDialog
        open={actionTarget?.action === "delete"}
        onOpenChange={(open) => {
          if (!open) {
            setActionTarget(null);
          }
        }}
        conversationTitle={actionTarget?.session.display_name ?? ""}
        onConfirm={confirmDelete}
      />
    </>
  );
};

interface FeaturedSessionRowProps {
  projectId: string;
  featuredSession: FeaturedSession;
  agent: Agent | undefined;
  isOwner: boolean;
  onUnfeature: () => void;
}

// A rail row links to the read-only view scoped to this project, so the
// sidebar/layout stay in place — the rail carries only a share token, never
// a conversation id, so this renders from that token rather than routing
// through a conversation.
const FeaturedSessionRow = ({
  projectId,
  featuredSession,
  agent,
  isOwner,
  onUnfeature,
}: FeaturedSessionRowProps) => {
  const agents = useFeaturedSessionServedAgents(featuredSession.share_token, agent);

  return (
    <li className="group relative cursor-pointer">
      <Link
        to={nextgenProjectFeaturedSessionPath(projectId, featuredSession.share_token)}
        className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-black/[0.06] hover:text-foreground dark:hover:bg-white/[0.08]"
      >
        {agents.length > 1 ? (
          <AgentAvatarStack agents={agents} size={18} />
        ) : agents[0] ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0">
                <AgentAvatar agent={agents[0]} size={18} />
              </span>
            </TooltipTrigger>
            <TooltipContent>{agents[0].display_name}</TooltipContent>
          </Tooltip>
        ) : (
          <div
            className="flex size-[18px] shrink-0 items-center justify-center text-current"
            aria-hidden="true"
          >
            <MessageSquare className="size-3" />
          </div>
        )}
        <span className="block min-w-0 flex-1 truncate pr-4">{featuredSession.display_name}</span>
      </Link>

      {isOwner && (
        <div className="absolute right-1 top-1/2 -translate-y-1/2">
          <ConversationActionsMenu onUnfeature={onUnfeature} />
        </div>
      )}
    </li>
  );
};

const FeaturedSessionsList = ({
  projectId,
  featuredSessions,
  attachedAgentsWithDetails,
  isOwner,
}: {
  projectId: string;
  featuredSessions: FeaturedSession[];
  attachedAgentsWithDetails: Agent[] | undefined;
  isOwner: boolean;
}) => {
  const agentsById = useFeaturedSessionAgents(featuredSessions, attachedAgentsWithDetails);
  const { unfeature } = useFeaturedSessionsMutations(projectId);

  return (
    <ul className="flex flex-col gap-0.5" role="list">
      {featuredSessions.map((featuredSession) => (
        <FeaturedSessionRow
          key={featuredSession.id}
          projectId={projectId}
          featuredSession={featuredSession}
          agent={featuredSession.agent_id ? agentsById.get(featuredSession.agent_id) : undefined}
          isOwner={isOwner}
          onUnfeature={() => void unfeature(featuredSession)}
        />
      ))}
    </ul>
  );
};

const ProjectNavLink = (props: {
  to: string;
  icon: ReactNode;
  label: ReactNode;
  end?: boolean;
  badge?: number;
}) => (
  <li>
    <SectionNavLink {...props} className="px-2 py-1.5" />
  </li>
);

const ProjectNavPlaceholder = ({ icon, label }: { icon: ReactNode; label: ReactNode }) => (
  <li>
    <SectionNavPlaceholder icon={icon} label={label} className="px-2 py-1.5" />
  </li>
);

export const ProjectSidebar = ({
  projectId,
  workbenchId,
  sessions,
  featuredSessions,
  attachedAgentsWithDetails,
  isOwner,
}: ProjectSidebarProps) => {
  const currentUser = useUser();
  const isWorkflowsAccessEnabled = useWorkflowsAccessFlag();
  const isWorksheetsEnabled = useWorksheetsFlag();

  // Each workbench-backed view is a link once the project has a workbench, and
  // an inert placeholder otherwise — one ternary is the whole graceful
  // degradation for projects that predate provisioning.
  const workbenchNavItem = (view: string, icon: ReactNode, label: ReactNode) =>
    workbenchId === null ? (
      <ProjectNavPlaceholder icon={icon} label={label} />
    ) : (
      <ProjectNavLink
        to={nextgenProjectWorkbenchPath(projectId, workbenchId, view)}
        icon={icon}
        label={label}
      />
    );

  return (
    <SectionSidebar ariaLabel="Project navigation">
      <ul className="flex flex-col gap-0.5 px-4 pt-4">
        <ProjectNavLink
          to={nextgenProjectPath(projectId)}
          end
          icon={<LayoutDashboard className="size-4" />}
          label="Overview"
        />
        {workbenchNavItem(
          `${RoutePath.FILES}/${BaseDirectory.PROJECT}`,
          <Files className="size-4" />,
          "Files"
        )}
        {workbenchNavItem(
          RoutePath.ENVIRONMENTS,
          <SquareTerminal className="size-4" />,
          <ComputeEnvsNavLabel workbenchId={workbenchId} />
        )}
        {isWorkflowsAccessEnabled &&
          workbenchNavItem(
            `${RoutePath.WORKFLOWS}/${WorkflowSubmissionStrings.BASE_ROUTE}`,
            <Workflow className="size-4" />,
            "Workflows"
          )}
        {isWorksheetsEnabled &&
          workbenchNavItem(
            RoutePath.WORKSHEETS,
            <LayersPlus className="size-4" />,
            "Analysis Worksheets"
          )}
        <ProjectNavLink
          to={nextgenProjectSettingsPath(projectId)}
          end
          icon={<Settings className="size-4" />}
          label="Settings"
        />
      </ul>

      <div className="flex flex-1 flex-col overflow-y-auto px-4 pb-2 pt-3">
        <SectionNavGroupLabel>Your Sessions</SectionNavGroupLabel>
        <NavLink
          to={nextgenProjectChatsPath(projectId)}
          end
          className={(state) => sectionNavLinkClassName(state, "mb-1 w-full px-2 py-1.5")}
        >
          <Plus className="size-4 shrink-0" />
          <span className="flex-1 text-left">New Session</span>
        </NavLink>
        {sessions === undefined ? (
          <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <li key={i} className="flex items-center gap-2 py-1.5">
                <Skeleton className="size-[18px] shrink-0 rounded-full" />
                <Skeleton className="h-3 w-28" />
              </li>
            ))}
          </ul>
        ) : sessions.length === 0 ? (
          <p className="py-1 text-xs italic text-muted-foreground/80">No sessions yet.</p>
        ) : (
          <>
            <RecentSessionsList
              projectId={projectId}
              sessions={sessions.slice(0, SESSIONS_SIDEBAR_LIMIT)}
              isOwner={isOwner}
              currentUserEmail={currentUser?.email}
              featuredSessions={featuredSessions}
              attachedAgentsWithDetails={attachedAgentsWithDetails}
            />
            {sessions.length > SESSIONS_SIDEBAR_LIMIT && (
              <Link
                to={nextgenProjectSessionsPath(projectId)}
                className="mt-1 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                View all
              </Link>
            )}
          </>
        )}

        <SectionNavGroupLabel className="pt-4">Featured Sessions</SectionNavGroupLabel>
        {featuredSessions === undefined ? (
          <ul className="flex flex-col gap-0.5" role="list" aria-busy="true">
            {Array.from({ length: 2 }).map((_, i) => (
              <li key={i} className="flex items-center gap-2 py-1.5">
                <Skeleton className="size-[18px] shrink-0 rounded-full" />
                <Skeleton className="h-3 w-28" />
              </li>
            ))}
          </ul>
        ) : featuredSessions.length === 0 ? (
          <p className="py-1 text-xs italic text-muted-foreground/80">No featured sessions.</p>
        ) : (
          <>
            <FeaturedSessionsList
              projectId={projectId}
              featuredSessions={featuredSessions.slice(0, SESSIONS_SIDEBAR_LIMIT)}
              attachedAgentsWithDetails={attachedAgentsWithDetails}
              isOwner={isOwner}
            />
            {featuredSessions.length > SESSIONS_SIDEBAR_LIMIT && (
              <Link
                to={nextgenProjectSessionsPath(projectId)}
                state={{ view: "featured" }}
                className="mt-1 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                View all
              </Link>
            )}
          </>
        )}
      </div>
    </SectionSidebar>
  );
};
